import { trpcClient } from '$src/lib/trpc'
import type { ServicePackageVersionWithTasks } from '@markku/types'
import { Group, List, Stack, Text } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { useQueries } from '@tanstack/react-query'

interface SubscriptionPackageListProps {
  subscriptionPackageVersions: Array<{
    quantity: number
    servicePackageVersion: {
      id: number
    }
  }>
}

export function SubscriptionPackageList({
  subscriptionPackageVersions,
}: SubscriptionPackageListProps) {
  const packageQueries = useQueries({
    queries: subscriptionPackageVersions.map((spv) => ({
      queryKey: [
        'servicePackageVersion.getById',
        { id: spv.servicePackageVersion.id },
      ],
      queryFn: () =>
        trpcClient.servicePackageVersion.getById.query({
          id: spv.servicePackageVersion.id,
        }),
      enabled: !!spv.servicePackageVersion.id,
    })),
  })

  const isLoadingPackages = packageQueries.some((q) => q.isLoading)

  // Combine fetched package details with quantity from junction table
  const packagesWithQuantity = packageQueries
    .map((q, index) => {
      const pkg = q.data
      const spv = subscriptionPackageVersions[index]
      if (!pkg) return null
      return {
        ...pkg,
        quantity: spv.quantity,
        totalPriceMonthly: (
          parseFloat(pkg.priceMonthly) * spv.quantity
        ).toFixed(2),
      }
    })
    .filter(
      (
        pkg,
      ): pkg is ServicePackageVersionWithTasks & {
        quantity: number
        totalPriceMonthly: string
      } => !!pkg,
    )

  if (isLoadingPackages || !packagesWithQuantity.length) {
    return null
  }

  return (
    <Stack gap="sm">
      <Text fw={500} size="sm" c="dimmed">
        Sisältö:
      </Text>
      <Stack gap="md">
        {packagesWithQuantity.map((pkg) => (
          <Stack key={pkg.id} gap={4}>
            <Group justify="space-between" wrap="nowrap">
              <Text fw={500}>
                {pkg.quantity > 1 && `${pkg.quantity} x `}
                {pkg.servicePackage?.name || 'Paketti'}
              </Text>
              <Group gap={4} align="baseline" style={{ whiteSpace: 'nowrap' }}>
                <Text size="xs" c="dimmed">
                  {pkg.quantity} x {pkg.priceMonthly} €/kk =
                </Text>
                <Text fw={500} c="green.7">
                  {pkg.totalPriceMonthly} €/kk
                </Text>
              </Group>
            </Group>
            {pkg.packageVersionTasks?.length ? (
              <List
                spacing={2}
                size="sm"
                withPadding
                pl="md"
                icon={
                  <IconCheck size={14} color="var(--mantine-color-green-6)" />
                }
                styles={{
                  item: { color: 'var(--mantine-color-dimmed)' },
                }}
              >
                {pkg.packageVersionTasks.map((task) => (
                  <List.Item key={task.id}>
                    <Group gap={4}>
                      <Text size="sm">{task.maintenanceTask?.name}</Text>
                      {task.frequency && (
                        <Text size="xs" c="dimmed">
                          ({task.frequency})
                        </Text>
                      )}
                    </Group>
                  </List.Item>
                ))}
              </List>
            ) : null}
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}
